import { useTheme, Box, Tooltip, styled } from '@mui/material'
import { EllipsisWithTooltip, Typography } from '@carto/meridian-ds/components'
import { JobStatusType } from 'features/jobs/types'
import { ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import JobItemIcon from './JobItemIcon'
import JobItemProgress from './JobItemProgress'
import { isPendingOrRunning } from 'features/jobs/models/jobs'

interface JobItemProps {
  status: JobStatusType
  name: string
  message?: ReactNode
  tooltip?: string
  showIcon?: boolean
  cursorPointer?: boolean
  disabled?: boolean
  padded?: boolean
  onClick?: () => void
  onCancel?: () => void
}

const RootBox = styled(Box, {
  shouldForwardProp: (prop: string) => !['cursorPointer', 'disabled', 'padded'].includes(prop)
})<{
  cursorPointer?: boolean
  disabled?: boolean
  padded?: boolean
}>(({ theme, cursorPointer, disabled, padded }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
  width: '100%',

  transition: theme.transitions.create('background-color', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard
  }),

  ...(cursorPointer && {
    cursor: 'pointer'
  }),
  ...(padded && {
    padding: theme.spacing(2)
  }),
  ...(cursorPointer &&
    padded && {
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }),

  ...(disabled && {
    cursor: 'default !important',
    pointerEvents: 'none'
  })
}))

const ProgressAndIconRow = styled(Box)(({ theme }) => ({
  minHeight: theme.spacing(3),

  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1.5),
  alignItems: 'center'
}))

function JobItem({
  status,
  name,
  message,
  tooltip,
  showIcon,
  padded,
  cursorPointer,
  disabled = false,
  onClick,
  onCancel
}: JobItemProps) {
  const theme = useTheme()
  const jobIsPending = isPendingOrRunning(status)
  const canCancel = onCancel && jobIsPending && !disabled
  const [titleRef, setTitleRef] = useState<HTMLSpanElement | null>(null)

  showIcon = showIcon ?? status !== JobStatusType.Cancelled

  const itemClickable = Boolean(onClick && !jobIsPending && status !== JobStatusType.Cancelled)

  cursorPointer = cursorPointer ?? !disabled

  const buttonTooltip = jobIsPending ? <FormattedMessage id="common.button.cancel" /> : tooltip

  const content = (
    <RootBox
      cursorPointer={itemClickable && cursorPointer}
      padded={padded}
      disabled={disabled}
      onClick={itemClickable ? onClick : undefined}
      aria-label={name}
      role="listitem"
    >
      <Typography variant="caption" noWrap={true} weight="medium" ref={setTitleRef}>
        {name}
      </Typography>
      <ProgressAndIconRow>
        <Box flex="1" display="flex" alignSelf="stretch" alignItems="center">
          <Box flex="1">
            <JobItemProgress jobStatus={status} />
          </Box>
        </Box>
        {showIcon && (
          <Box
            flex="0"
            style={{
              width: theme.spacing(4)
            }}
          >
            <JobItemIcon
              status={status}
              tooltip={canCancel ? buttonTooltip : undefined}
              onClick={!disabled && canCancel ? onCancel : undefined}
            />
          </Box>
        )}
      </ProgressAndIconRow>

      <Typography variant="caption" color="textSecondary">
        <EllipsisWithTooltip>{message}</EllipsisWithTooltip>
      </Typography>
    </RootBox>
  )

  if (!itemClickable || !tooltip) {
    return content
  }
  return (
    <Tooltip title={tooltip} placement="top" arrow PopperProps={{ anchorEl: titleRef }}>
      {content}
    </Tooltip>
  )
}

export default JobItem
